import { Link } from 'react-router-dom';
import { GalleryVertical, Trophy } from 'lucide-react';
import { useMyResults } from '@/hooks/useJudging';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody } from '@/components/ui/Card';
import { SubmissionStatus, type MyResult } from '@/api/types';
import { submissionStatusLabel, submissionStatusTone } from '@/lib/submissions';

/** "7,25" - Slovenian decimal comma, two decimals. */
const formatScore = (score: number) => score.toFixed(2).replace('.', ',');

function ResultCard({ result }: { result: MyResult }) {
  const scored = result.resultsPublished && result.rank != null && result.averageScore != null;

  return (
    <Card>
      <CardBody className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <Link to={`/submissions/${result.submissionId}`} className="font-medium text-fg hover:underline">
              {result.title}
            </Link>
            <p className="text-sm text-fg-muted">
              <Link to={`/competitions/${result.competitionId}`} className="hover:underline">
                {result.competitionName}
              </Link>
              {result.categoryName && <> &middot; {result.categoryName}</>}
            </p>
          </div>
          <Badge tone={submissionStatusTone(result.status)}>{submissionStatusLabel(result.status)}</Badge>
        </div>

        {(result.awards.length > 0 || result.isSelected) && (
          <ul className="flex flex-wrap gap-2">
            {result.isSelected && (
              <li className="inline-flex items-center gap-1.5 rounded-control border border-line-strong bg-surface-2 px-2.5 py-1 text-sm font-medium text-fg">
                <GalleryVertical size={14} aria-hidden="true" />
                Izbrano za razstavo
              </li>
            )}
            {result.awards.map((award) => (
              <li
                key={award.title}
                className="inline-flex items-center gap-1.5 rounded-control border border-accent-ring bg-accent-subtle px-2.5 py-1 text-sm font-medium text-accent-text"
              >
                <Trophy size={14} aria-hidden="true" />
                {award.title}
              </li>
            ))}
          </ul>
        )}

        {scored ? (
          <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-fg-muted">Povprečna ocena</dt>
              <dd className="font-mono text-lg font-semibold tabular-nums text-fg">{formatScore(result.averageScore!)}</dd>
            </div>
            <div>
              <dt className="text-fg-muted">Mesto v kategoriji</dt>
              <dd className="font-mono text-lg font-semibold tabular-nums text-fg">
                {result.categoryRank} <span className="text-sm font-normal text-fg-muted">/ {result.categoryEntries}</span>
              </dd>
            </div>
            <div>
              <dt className="text-fg-muted">Mesto na tekmovanju</dt>
              <dd className="font-mono text-lg font-semibold tabular-nums text-fg">
                {result.rank} <span className="text-sm font-normal text-fg-muted">/ {result.rankedEntries}</span>
              </dd>
            </div>
            <div>
              <dt className="text-fg-muted">Število ocen</dt>
              <dd className="font-mono text-lg font-semibold tabular-nums text-fg">{result.scoreCount}</dd>
            </div>
          </dl>
        ) : result.status === SubmissionStatus.Rejected ? (
          <p className="text-sm text-fg-secondary">
            Prispevek je bil zavrnjen{result.rejectionReason ? `: ${result.rejectionReason}` : '.'}
          </p>
        ) : result.resultsPublished ? (
          <p className="text-sm text-fg-muted">Prispevek ni bil ocenjen.</p>
        ) : (
          <p className="text-sm text-fg-muted">Rezultati bodo objavljeni, ko je ocenjevanje zaključeno.</p>
        )}
      </CardBody>
    </Card>
  );
}

export function ResultsPage() {
  const results = useMyResults();

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-fg">Moji rezultati</h1>
        <p className="text-sm text-fg-muted">
          Ocene, mesta in nagrade vaših prispevkov. Prikažejo se, ko je ocenjevanje na tekmovanju zaključeno.
        </p>
      </div>

      {results.isLoading && <PageSpinner />}
      {results.isError && <ErrorState message={getApiErrorMessage(results.error)} onRetry={() => results.refetch()} />}
      {results.isSuccess && results.data.items.length === 0 && (
        <EmptyState title="Ni še rezultatov" description="Rezultati se prikažejo, ko oddate prispevke in so ti ocenjeni." />
      )}
      {results.isSuccess && results.data.items.length > 0 && (
        <div className="flex flex-col gap-3">
          {results.data.items.map((result) => (
            <ResultCard key={result.submissionId} result={result} />
          ))}
        </div>
      )}
    </div>
  );
}
